import type { ApiUser } from "@hive/sdk";
import { resolveConfig } from "../config";
import type { PlayableKind } from "./playableKind";

type SensorsSdk = (typeof import("sa-sdk-javascript"))["default"];

const PLATFORM_TYPE = "muhaha.ai";
const APP_VERSION = "0.1.0";
/**
 * Cross-client join key. The app registers the SAME literal in its super props
 * (muhaha-app-client/loopit-app/lib/analytics.ts), so web and app events land in one
 * analyzable set in 神策. Named `app_slug` rather than `app_id` to keep it distinct from
 * SA's preset `$app_id`, which the app fills with its per-platform bundle id. Keep the two in sync.
 */
const APP_SLUG = "muhaha";

type EventResult = "success" | "failure";
type ContentAction = "like" | "unlike" | "favorite" | "unfavorite" | "share" | "comment" | "remix";

export interface ContentProperties {
	content_id: string;
	content_kind: PlayableKind;
	source?: string;
	position?: number;
	feed_tab?: string;
	category?: string;
}

interface AnalyticsEventMap {
	auth_modal_opened: Record<string, never>;
	auth_code_requested: { result: EventResult; error_code?: string };
	login_result: {
		method: "email" | "google" | "apple" | "guest";
		result: EventResult;
		user_kind?: string;
		error_code?: string;
	};
	logout: { result: EventResult; error_code?: string };
	referral_captured: { source: "invite_path" | "ref_query" | "manual_entry" };
	referral_banner_shown: Record<string, never>;
	referral_signup_attributed: { method: "email" | "google" | "apple" };
	content_impression: ContentProperties;
	content_opened: ContentProperties;
	content_action_result: ContentProperties & {
		action: ContentAction;
		result: EventResult;
		error_code?: string;
	};
	/** Paid economy (VITE_ENABLE_PAID_ECONOMY): Stripe Checkout redirect started. */
	checkout_started: {
		product_id: string;
		amount: number;
		currency: string;
		result: EventResult;
		error_code?: string;
	};
	/** Paid economy: outcome of the Stripe return-page order poll. */
	topup_result: {
		order_id: string;
		amount?: number;
		currency: string;
		result: "success" | "cancel" | "expired" | "pending";
	};
	/** Paid economy: a credits pack bought with Mubux. */
	credit_pack_purchase_result: {
		product_id: string;
		amount: number;
		currency: string;
		granted_credits: number;
		result: EventResult;
		error_code?: string;
	};
	playable_load_result: {
		playable_id: string;
		load_ms: number;
		result: EventResult;
		error_code?: string;
	};
	/**
	 * 首帧闸门: cover time added AFTER the frame loaded, for a playable whose
	 * `spec.haveReadyEvent` promises `play.ready()`. Reported only when a wait
	 * actually happened — a signal that beat `load` held nothing back.
	 */
	playable_ready_wait: {
		playable_id: string;
		outcome: "ready" | "timeout";
		wait_ms: number;
		timeout_ms: number;
	};
	playable_session_end: {
		playable_id: string;
		duration_ms: number;
		exit_reason: "unmount" | "switch" | "load_failure";
	};
	/** Handoff to Muhaha Create (`/create` → editor); generation and publish results are its events now. */
	creation_started: {
		content_kind: PlayableKind;
		mode: "remix" | "quick";
	};
	/**
	 * Bridge-carried in-playable event — window.muhaha.analytics.track calls and
	 * the bundle runtime's __muhaha_rt session-health drain (play/telemetry.ts).
	 * ONE fixed 神策 event for every playable: the game's own event name rides as
	 * `playable_event_name` so dynamic playables don't mint unbounded event
	 * definitions; the game's scalar properties ride alongside.
	 */
	playable_event: {
		playable_event_name: string;
		playable_id: string;
		playable_mode?: string;
	} & Record<string, string | number | boolean | undefined>;
	open_in_app_banner_shown: OpenInAppProperties;
	open_in_app_click: OpenInAppProperties;
	open_in_app_get_app: OpenInAppProperties;
	/** `/open/*` share deep link forwarded to its web route (router/index.tsx). */
	deeplink_redirect: { target: string; source: string };
	/**
	 * The game called `ads.show` — the user tapped watch-ad (MUHAHA-830). Rewarded
	 * funnel top: followed by exactly one `ad_declined`, `ad_request`, or (`reclaim`)
	 * `ad_reward_result`. From the SDK bridge's `ads.onOutcome`.
	 */
	ad_click: AdEventProperties & { placement: "rewarded"; offer_id: string; reclaim: boolean };
	ad_request: AdEventProperties;
	/** `wait_ms` = IMA's own request→STARTED; `click_wait_ms` = tap→STARTED, what the user waited (rewarded). */
	ad_started: AdEventProperties & { wait_ms?: number; click_wait_ms?: number };
	ad_quartile: AdEventProperties & { quartile: number };
	ad_complete: AdEventProperties;
	ad_skipped: AdEventProperties;
	ad_error: AdEventProperties & { stage?: string; error_code?: number };
	ad_closed: AdEventProperties & {
		result: "completed" | "dismissed" | "no_fill" | "error";
		duration_ms?: number;
		watch_ms?: number;
	};
	/**
	 * No ad was asked for: the SDK bridge refused before reaching the host adapter
	 * (server verdict, interstitial cooldown, busy/unsupported host). `stage` says
	 * where the flow stopped, `reason` why — the same vocabulary the game sees.
	 * `start_delay` is the 开局静默期; the game is told plain `cooldown` either way,
	 * so the stage is the only place the two 插屏 gates can be told apart.
	 */
	ad_declined: AdEventProperties & {
		stage: "ready" | "session" | "cooldown" | "start_delay" | "busy" | "unsupported";
		reason: string;
		remaining_today?: number;
		retry_in_seconds?: number;
	};
	/** The rewarded settle call answered (`economy.adClaim`). `granted` is the server's verdict. */
	ad_reward_result: AdEventProperties & {
		placement: "rewarded";
		offer_id: string;
		ad_session_id: string;
		granted: boolean;
		reason?: string;
		amount?: number;
		/** Ad closed (or re-claim began) → verdict, late-SSV retries included. */
		settle_ms: number;
	};
}

/**
 * Shared by every `ad_*` event. `placement` is the SDK's vocabulary
 * (`SandboxAdPlacement`), which the app's AdMob events report too — web's own
 * IMA config keys (gameReward/gamePreroll/gameInterstitial) stay out of 神策.
 * `offer_id` / `ad_session_id` / `session_ms` exist for rewarded ads only.
 */
interface AdEventProperties {
	provider: "ima";
	placement: "rewarded" | "break" | "preroll";
	playable_id?: string;
	offer_id?: string;
	ad_session_id?: string;
	/** Tap → this adapter: the `economy.adSession` round trip (MUHAHA-830). */
	session_ms?: number;
}

interface OpenInAppProperties {
	playable_id: string;
	platform: "ios" | "android";
}

type AnalyticsProperties = Record<string, unknown>;

interface PageDescriptor {
	page_name: string;
	route_template: string;
	content_id?: string;
}

let initialized = false;
let sensorsLoadFailed = false;
let sensors: SensorsSdk | null = null;
let pending: Array<(sdk: SensorsSdk) => void> = [];
let activeUserId: string | null = null;
let lastPagePath = "";
let lastPageUrl = "";
let impressionScope = 0;
const impressions = new Set<string>();

/**
 * Runs `job` once the sensors SDK is ready. The 174KB SDK is loaded via dynamic
 * import off the boot critical path, so calls made before it resolves are queued
 * and replayed in order; when analytics is disabled they are dropped.
 */
function withSensors(label: string, job: (sdk: SensorsSdk) => void) {
	if (!initialized || sensorsLoadFailed) return;
	const run = (sdk: SensorsSdk) => {
		try {
			job(sdk);
		} catch (error) {
			if (import.meta.env.DEV) console.warn(label, error);
		}
	};
	if (sensors) run(sensors);
	else pending.push(run);
}

export function initAnalytics() {
	if (initialized || typeof window === "undefined") return;
	const config = resolveConfig();
	if (!config.sensorsEnabled || !config.sensorsServerUrl) return;
	initialized = true;

	void import("sa-sdk-javascript")
		.then(({ default: sdk }) => {
			try {
				setupSensors(sdk, config);
			} catch (error) {
				if (import.meta.env.DEV) console.warn("Sensors Analytics initialization failed", error);
				sensorsLoadFailed = true;
				pending = [];
				return;
			}
			sensors = sdk;
			const jobs = pending;
			pending = [];
			for (const job of jobs) job(sdk);
		})
		.catch((error: unknown) => {
			if (import.meta.env.DEV) console.warn("Sensors Analytics SDK load failed", error);
			sensorsLoadFailed = true;
			pending = [];
		});
}

function setupSensors(sdk: SensorsSdk, config: ReturnType<typeof resolveConfig>) {
	// Built-in (bundled, off by default) plugin: emits `$WebPageLeave` with `event_duration` on
	// route switch / tab hide / pagehide, with a heartbeat that reissues leaves lost to a killed tab.
	// It hooks the SDK's own pushState/popstate patch, so react-router navigation is covered.
	const pageLeave = sdk.use("PageLeave", {
		heartbeat_interval_time: 5,
		max_duration: 24 * 60 * 60,
	}) as {
		addPageEndListener(): void;
		pageEndHandler(): void;
		stopHeartBeatInterval(): void;
	};
	// sa-sdk-javascript 1.27.13 hardcodes unload listeners, which Permissions Policy can block.
	// use() defers plugin initialization until init(), so replace only its page-end binding first.
	pageLeave.addPageEndListener = function () {
		window.addEventListener("pagehide", () => {
			this.pageEndHandler();
			this.stopHeartBeatInterval();
		});
	};
	sdk.init({
		server_url: config.sensorsServerUrl,
		show_log: import.meta.env.DEV,
		is_track_single_page: false,
		cross_subdomain: false,
		is_track_device_id: false,
		preset_properties: {
			url: false,
			title: true,
			latest_utm: true,
			latest_traffic_source_type: true,
			latest_search_keyword: false,
			latest_referrer: false,
			latest_referrer_host: false,
			latest_landing_page: false,
			search_keyword_baidu: false,
		},
		heatmap: {
			clickmap: "default",
			scroll_notice_map: "not_collect",
			// Keep click analytics structural: never send typed values, visible text (which can
			// contain chat/comment content), or URL query parameters with `$WebClick`.
			collect_input: () => false,
			custom_property: (element: Element) => {
				const targetUrl = element.getAttribute("href");
				return {
					$url: analyticsUrl(window.location.href),
					$element_content: "",
					...(targetUrl ? { $element_target_url: analyticsUrl(targetUrl) } : {}),
				};
			},
		},
	});
	// Read the landing URL before the router redirects `/open/*` away, so the share/campaign
	// properties ride on every event of the session (the SDK's own latest_utm preset only covers
	// utm_*; share_said/share_ssuid/sharer come from our own l_data blob).
	const attribution = getLandingAttribution();
	sdk.registerPage({
		app_slug: APP_SLUG,
		platform_type: PLATFORM_TYPE,
		app_env: config.appEnv,
		app_version: APP_VERSION,
		client_type: "web",
		locale: () => document.documentElement.lang || navigator.language || "unknown",
		is_logged_in: false,
		user_kind: "anonymous",
		...attribution.properties,
	});
}

/** Campaign/share attribution carried by the URL the visit landed on. */
export interface LandingAttribution {
	/** Registered as 神策 preset properties: `utm_*` plus `share_said`/`share_ssuid`/`sharer`. */
	properties: Record<string, string>;
	/** `utm_*` only — the payload the Play Store forwards to a fresh install as `referrer`. */
	utmQuery: string;
	/** `utm_*` plus the raw `l_data` blob — appended to the share links this site mints. */
	shareQuery: string;
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

/**
 * Parses one landing query string into attribution. `l_data` is minted by whoever shared the link,
 * so a malformed blob degrades to "no share attribution" instead of throwing — this runs inside
 * analytics init, and a throw there costs the whole session's tracking.
 */
export function parseLandingAttribution(search: string): LandingAttribution {
	const params = new URLSearchParams(search);
	const properties: Record<string, string> = {};
	const utm = new URLSearchParams();

	for (const key of UTM_KEYS) {
		const value = readString(params.get(key));
		if (!value) continue;
		properties[key] = value;
		utm.set(key, value);
	}

	const lData = params.get("l_data") ?? "";
	const shared = parseLData(lData);
	if (shared.said) properties.share_said = shared.said;
	if (shared.ssuid) properties.share_ssuid = shared.ssuid;
	if (shared.sharer) properties.sharer = shared.sharer;

	const share = new URLSearchParams(utm);
	if (lData) share.set("l_data", lData);

	return { properties, utmQuery: utm.toString(), shareQuery: share.toString() };
}

let landingAttribution: LandingAttribution | null = null;

/**
 * Attribution of the URL this visit landed on, captured on first call and then frozen — later
 * client-side navigation must not change what the session is attributed to.
 */
export function getLandingAttribution(): LandingAttribution {
	landingAttribution ??= parseLandingAttribution(
		typeof window === "undefined" ? "" : window.location.search,
	);
	return landingAttribution;
}

/**
 * Merges an incoming query onto a redirect target, so `/open/game/:id?l_data=…&utm_source=…`
 * keeps its attribution across the hop to `/play/:id`. The target's own params win, which is what
 * keeps the invite route's `ref=<code>` (taken from the path) from being clobbered by the query.
 */
export function mergeAttributionSearch(path: string, search: string): string {
	const incoming = new URLSearchParams(search);
	if (![...incoming.keys()].length) return path;

	const mark = path.indexOf("?");
	const pathname = mark === -1 ? path : path.slice(0, mark);
	const merged = new URLSearchParams(mark === -1 ? "" : path.slice(mark + 1));
	for (const [key, value] of incoming) {
		if (!merged.has(key)) merged.append(key, value);
	}

	const query = merged.toString();
	return query ? `${pathname}?${query}` : pathname;
}

function parseLData(raw: string): { said?: string; ssuid?: string; sharer?: string } {
	if (!raw) return {};
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return {};
		const record = parsed as Record<string, unknown>;
		return {
			said: readString(record.said),
			ssuid: readString(record.ssuid),
			sharer: readString(record.sharer),
		};
	} catch {
		return {};
	}
}

function readString(value: unknown): string | undefined {
	if (typeof value === "string") return value.trim() || undefined;
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	return undefined;
}

export function track<K extends keyof AnalyticsEventMap>(
	event: K,
	properties: AnalyticsEventMap[K],
) {
	withSensors(`Sensors Analytics event failed: ${event}`, (sdk) => {
		sdk.track(event, cleanProperties(properties as AnalyticsProperties));
	});
}

export function setAnalyticsUser(user: ApiUser) {
	withSensors("Sensors Analytics identity sync failed", (sdk) => {
		if (activeUserId !== user.id) {
			sdk.login(user.id);
			activeUserId = user.id;
		}
		const userKind = user.kind.toLowerCase();
		sdk.registerPage({ is_logged_in: true, user_kind: userKind });
		sdk.setProfile({
			platform_type: PLATFORM_TYPE,
			user_kind: userKind,
			locale: user.locale ?? "unknown",
		});
	});
}

export function clearAnalyticsUser(force = false) {
	withSensors("Sensors Analytics logout failed", (sdk) => {
		if (!activeUserId && !force) return;
		sdk.logout(true);
		sdk.registerPage({ is_logged_in: false, user_kind: "anonymous" });
		activeUserId = null;
	});
}

export function trackPageView(pathname: string) {
	if (!initialized || pathname === lastPagePath) return;
	const descriptor = describePage(pathname);
	const pageUrl = `${window.location.origin}${pathname}`;
	const referrer = lastPageUrl || safeReferrer(document.referrer);
	lastPagePath = pathname;
	lastPageUrl = pageUrl;
	impressionScope += 1;
	impressions.clear();

	withSensors("Sensors Analytics page view failed", (sdk) => {
		sdk.quick("autoTrackSinglePage", {
			...descriptor,
			$url: pageUrl,
			$url_path: pathname,
			$referrer: referrer,
			$title: descriptor.page_name,
			not_set_profile: true,
		});
	});
}

export function trackContentImpression(properties: ContentProperties) {
	const key = [
		impressionScope,
		properties.content_id,
		properties.source ?? "unknown",
		properties.feed_tab ?? "unknown",
		properties.category ?? "unknown",
	].join(":");
	if (impressions.has(key)) return;
	impressions.add(key);
	track("content_impression", properties);
}

export function analyticsErrorCode(error: unknown): string {
	if (error && typeof error === "object") {
		const code = Reflect.get(error, "code");
		if (typeof code === "string" && code) return code.slice(0, 100);
		if (error instanceof Error && error.name) return error.name.slice(0, 100);
	}
	return "unknown";
}

export function analyticsPageName(pathname: string): string {
	return describePage(pathname).page_name;
}

function cleanProperties(properties: AnalyticsProperties): AnalyticsProperties {
	return Object.fromEntries(
		Object.entries(properties).filter(([, value]) => value !== undefined && value !== null),
	);
}

function safeReferrer(value: string): string {
	if (!value) return "";
	try {
		const url = new URL(value);
		return url.origin === window.location.origin ? `${url.origin}${url.pathname}` : url.origin;
	} catch {
		return "";
	}
}

function analyticsUrl(value: string): string {
	try {
		const url = new URL(value, window.location.origin);
		return url.protocol === "http:" || url.protocol === "https:"
			? `${url.origin}${url.pathname}`
			: "";
	} catch {
		return "";
	}
}

function describePage(pathname: string): PageDescriptor {
	const contentRoutes: Array<[RegExp, string, string]> = [
		[/^\/messages\/([^/]+)$/, "direct_message", "/messages/:id"],
		[/^\/play\/([^/]+)$/, "playable", "/play/:id"],
		[/^\/drama\/([^/]+)$/, "drama", "/drama/:id"],
		// `/open/*` share deep links redirect away immediately, but the landing still
		// gets a page view — anchored so they can't swallow other paths.
		[/^\/open\/game\/([^/]+)$/, "open_game", "/open/game/:id"],
		[/^\/open\/drama\/([^/]+)$/, "open_drama", "/open/drama/:id"],
		[/^\/open\/invite\/([^/]+)$/, "open_invite", "/open/invite/:code"],
	];
	for (const [pattern, pageName, routeTemplate] of contentRoutes) {
		const match = pathname.match(pattern);
		if (match) return { page_name: pageName, route_template: routeTemplate, content_id: match[1] };
	}

	const staticRoutes: Record<string, [string, string]> = {
		"/": ["discover", "/"],
		"/all/tags": ["all_tags", "/all/tags"],
		"/all/tags/": ["all_tags", "/all/tags"],
		"/search": ["search", "/search"],
		"/create": ["create", "/create"],
		"/library": ["library", "/library"],
		"/friends": ["friends", "/friends"],
		"/wallet": ["wallet", "/wallet"],
		"/notifications": ["notifications", "/notifications"],
		"/settings": ["settings", "/settings"],
		"/about": ["about", "/about"],
		// page_name stays "terms" across the /terms → /tos rename so the funnel doesn't split.
		"/tos": ["terms", "/tos"],
		"/terms": ["terms", "/terms"],
		"/privacy": ["privacy", "/privacy"],
	};
	const route = staticRoutes[pathname];
	if (route) return { page_name: route[0], route_template: route[1] };
	if (/^\/u\/[^/]+$/.test(pathname)) return { page_name: "profile", route_template: "/u/:handle" };
	// Topic browsing used to be `/?topic=x` and reported as "discover"; keep it a named page.
	if (/^\/t\/[^/]+$/.test(pathname)) return { page_name: "topic", route_template: "/t/:tag" };
	return { page_name: "unknown", route_template: "unknown" };
}
